import { RefObject } from "react";
import { useShallow } from "zustand/react/shallow";

import TableCellEmpty from "ds/components/Table/Row/CellEmpty";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleStore } from "ds/components/TableSimple/types";

import SpaceDetailsAccessGrantedViaCell from "../components/AccessCells/GrantedViaCell";
import SpaceDetailsAccessGrantedViaSummaryCell from "../components/AccessCells/GrantedViaSummaryCell";
import SpaceDetailsAccessNameCell from "../components/AccessCells/NameCell";
import SpaceDetailsAccessRoleCell from "../components/AccessCells/RoleCell";
import SpaceDetailsAccessRoleSummaryCell from "../components/AccessCells/RoleSummaryCell";
import { SpaceDetailsAccessRoleBinding } from "../components/AccessCells/types";
import SpaceDetailsExpandableStickyRows, {
  ExpandableStickyRowsContext,
} from "../components/ExpandableStickyRows";
import { SPACE_USERS_COLUMN_ID } from "./constants";
import { SpaceDetailsUser } from "./types";

type SpaceDetailsUsersRowsProps = {
  data: SpaceDetailsUser[];
  store: TableSimpleStore;
  tableRef: RefObject<HTMLDivElement | null>;
};

const getUserId = (user: SpaceDetailsUser) => user.id;
const getUserRoleBindings = (user: SpaceDetailsUser) => user.roleBindings;
const getRoleBindingId = (binding: SpaceDetailsAccessRoleBinding) => binding.id;

const SpaceDetailsUsersRows = ({ data, store, tableRef }: SpaceDetailsUsersRowsProps) => {
  const columns = store(useShallow(selectVisibleColumns));

  const renderParentCells = (
    user: SpaceDetailsUser,
    { isExpandable, isExpanded, onToggle }: ExpandableStickyRowsContext
  ) => {
    const singleBinding = user.roleBindings[0];

    return columns.map((column, i) => {
      const cellId = `${user.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_USERS_COLUMN_ID.NAME:
          return (
            <SpaceDetailsAccessNameCell
              key={cellId}
              id={cellId}
              name={user.name}
              href={`/settings/user/${user.id}`}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
              onToggle={onToggle}
            />
          );
        case SPACE_USERS_COLUMN_ID.ROLE:
          if (isExpandable) {
            return (
              <SpaceDetailsAccessRoleSummaryCell key={cellId} {...cellLayoutProps} item={user} />
            );
          }
          if (singleBinding) {
            return (
              <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={singleBinding} />
            );
          }
          return <TableSimpleCell key={cellId} {...cellLayoutProps} />;
        case SPACE_USERS_COLUMN_ID.GRANTED_VIA:
          if (isExpandable) {
            return (
              <SpaceDetailsAccessGrantedViaSummaryCell
                key={cellId}
                {...cellLayoutProps}
                item={user}
              />
            );
          }
          if (singleBinding) {
            return (
              <SpaceDetailsAccessGrantedViaCell
                key={cellId}
                {...cellLayoutProps}
                item={singleBinding}
              />
            );
          }
          return <TableSimpleCell key={cellId} {...cellLayoutProps} />;
        default:
          return null;
      }
    });
  };

  const renderChildCells = (binding: SpaceDetailsAccessRoleBinding, user: SpaceDetailsUser) =>
    columns.map((column, i) => {
      const cellId = `${user.id}-${binding.id}-${column.id}`;
      const cellLayoutProps = { id: cellId, isFirst: i === 0 };

      switch (column.id) {
        case SPACE_USERS_COLUMN_ID.ROLE:
          return <SpaceDetailsAccessRoleCell key={cellId} {...cellLayoutProps} item={binding} />;
        case SPACE_USERS_COLUMN_ID.GRANTED_VIA:
          return (
            <SpaceDetailsAccessGrantedViaCell key={cellId} {...cellLayoutProps} item={binding} />
          );
        default:
          // Columns without a per-binding value (the group column) show a dash; the connector
          // line is drawn on the child row's first cell via CSS, whichever column that is.
          return (
            <TableSimpleCell key={cellId} id={cellId} role={i === 0 ? "rowheader" : undefined}>
              <TableCellEmpty />
            </TableSimpleCell>
          );
      }
    });

  return (
    <SpaceDetailsExpandableStickyRows
      data={data}
      tableRef={tableRef}
      getParentId={getUserId}
      getChildren={getUserRoleBindings}
      getChildId={getRoleBindingId}
      renderParentCells={renderParentCells}
      renderChildCells={renderChildCells}
    />
  );
};

export default SpaceDetailsUsersRows;
